import { addError, addLoading, getLoading, removeLoadingAndError } from '../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { IAppState } from '../core/redux/IAppState';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { apiTrainingDocumentById } from '../core/redux/api/endpoints';
import { ACTION_TYPE } from '../core/redux/actionType';
import { sortTrainingDocuments, TrainingDocument } from './trainingDocument';
import { assoc } from '../language/module';


const KEY_ACTION = ACTION_TYPE.REPLACE_TRAINING_DOC_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.REPLACE_TRAINING_DOC_REQUEST,
  ACTION_TYPE.REPLACE_TRAINING_DOC_RESPONSE,
  ACTION_TYPE.REPLACE_TRAINING_DOC_ERROR,
];


/**
 * Creates an action to dispatch that replaces a training document's file and/or metadata. The API treats the file as
 * optional — omitting it updates title/sortOrder only. Multipart form data: headers deliberately omit content-type so
 * the browser sets the multipart boundary.
 * @param documentId the document to replace
 * @param title display title for the document list
 * @param sortOrder optional position in the document list (unset sorts last)
 * @param file the replacement .docx, if the file itself is changing
 */
export const replaceTrainingDocumentAction = (documentId: number, title: string, sortOrder?: number, file?: File) => makeApiAction(
  {
    method:   'PUT',
    endpoint: (state: IAppState) => `${apiTrainingDocumentById(documentId)}?role=${encodeURIComponent(state.userState?.role ?? '')}`,
    headers:  (state: IAppState) => ({
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body:     (state: IAppState) => {
      const formData = new FormData();
      if (file) {
        formData.append('File', file);
      }
      formData.append('Title', title);
      if (sortOrder != null) {
        formData.append('SortOrder', String(sortOrder));
      }
      formData.append('UploadedBy', state.userState.userName);
      return formData;
    },
    types:    ACTIONS.map(action => ({ type: action, meta: { documentId } })) as RsaaTypes,
    bailout:  state => !!getLoading(state, String(documentId), KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<TrainingDocument> = (state, action, error, isLoading, payload) => {
  const documentId = String(action.meta.documentId);

  if (isLoading) {
    return addLoading(state, documentId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, documentId, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, documentId, KEY_ACTION);

  return assoc(
    'trainingDocuments',
    sortTrainingDocuments((noLoadingOrError.trainingDocuments ?? []).map(doc => doc.id === payload!.id ? payload! : doc)),
    noLoadingOrError,
  );
};


export const replaceTrainingDocumentReducer = makeApiReducer<TrainingDocument>(ACTIONS, KEY_ACTION, reducer);
